<template>
  <q-page class="bg-page q-pa-xl">
    <div class="container-xl">
      <!-- HEADER -->
      <div class="header-section q-mb-lg flex justify-between items-center">
        <div class="flex items-center">
          <h5 class="text-mint text-bold q-mb-none">Преглед по фази</h5>
        </div>
      </div>

      <!-- INFO STRIP -->
      <div class="info-strip q-mb-lg shadow-1">
        <div class="info-item">
          <q-icon name="person" size="18px" class="text-mint q-mr-xs" />
          <b>{{ userFullName }}</b>
        </div>

        <div class="info-item">
          <q-icon name="event" size="18px" class="text-mint q-mr-xs" />
          {{ formatDisplayDate(new Date()) }}
        </div>

        <div class="info-item">
          <q-icon name="list_alt" size="18px" class="text-mint q-mr-xs" />
          Активни фази: <b>{{ activeCount }}</b>
        </div>

        <div class="info-item">
          Одржување: <b>{{ odrzuvanjeCount }}</b>
        </div>
      </div>

      <!-- FILTERS -->
      <q-card flat bordered class="filter-card q-pa-md q-mb-lg">
        <div class="row q-col-gutter-md">
          <div class="col-12 col-md-4">
            <q-input
              v-model="searchId"
              outlined
              dense
              color="mint"
              label="Пребарај по број"
              clearable
              maxlength="10"
              @update:model-value="onSearchIdInput"
            >
              <template v-slot:prepend>
                <q-icon name="search" />
              </template>
            </q-input>
          </div>

          <div class="col-12 col-md-4">
            <q-select
              v-model="filterFazaId"
              :options="faziOptions"
              label="Фази"
              outlined
              dense
              color="mint"
              emit-value
              map-options
              clearable
            />
          </div>

          <div class="col-12 col-md-4">
            <q-select
              v-model="filterPrimilId"
              :options="employeeOptions"
              label="Вработени"
              outlined
              dense
              color="mint"
              emit-value
              map-options
              clearable
            />
          </div>
        </div>
      </q-card>

      <!-- TABLE -->
      <q-card flat bordered class="table-card q-pa-md">
        <q-table
          :data="filtered"
          :columns="columns"
          row-key="ID"
          flat
          dense
          class="rounded-xl table-clean clickable-table"
          no-data-label="Нема активни фази за прикажување."
          :loading="loading"
          @row-click="openManageDialog"
        >
          <!-- Датум -->
          <template v-slot:body-cell-Datum="props">
            <q-td align="center">{{ formatDisplayDate(props.row.Datum) }}</q-td>
          </template>

          <!-- Фирма -->
          <template v-slot:body-cell-PartnerID="props">
            <q-td align="left">{{ props.row.NazivPartner || "" }}</q-td>
          </template>

          <!-- Опис -->
          <template v-slot:body-cell-Opis="props">
            <q-td align="left">
              <div class="opis-preview">
                {{ shortText(props.row.Opis, 55) }}
              </div>
            </q-td>
          </template>

          <!-- Апликација -->
          <template v-slot:body-cell-AplikacijaID="props">
            <q-td align="center">
              {{
                props.row.OpisAplikacija ||
                (aplikaciiMap[props.row.AplikacijaID] || props.row.AplikacijaID)
              }}
            </q-td>
          </template>

          <!-- Итност -->
          <template v-slot:body-cell-StepenItnost="props">
            <q-td align="center">
              {{ props.row.StepenItnost ?? "" }}
            </q-td>
          </template>

          <!-- СТАТУС -->
          <template v-slot:body-cell-StatusID="props">
            <q-td align="center">
              {{ getStatusText(props.row) }}
            </q-td>
          </template>

          <!-- Документи -->
          <template v-slot:body-cell-Files="props">
            <q-td align="center" @click.stop>
              <q-icon
                v-if="!props.row.Files || props.row.Files.length === 0"
                name="attach_file"
                color="grey-5"
                size="22px"
              >
                <q-tooltip>Нема прикачени фајлови</q-tooltip>
              </q-icon>

              <q-btn v-else flat round dense color="mint" icon="attach_file">
                <q-tooltip>{{ props.row.Files.length }} фајлови</q-tooltip>

                <q-menu>
                  <q-list dense bordered>
                    <q-item
                      v-for="(file, idx) in props.row.Files"
                      :key="idx"
                      clickable
                      @click="previewFile(file)"
                    >
                      <q-item-section avatar>
                        <q-icon name="insert_drive_file" color="mint" />
                      </q-item-section>
                      <q-item-section>{{ file.split('/').pop() }}</q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </q-td>
          </template>

          <!-- Тековна фаза -->
          <template v-slot:body-cell-TekovnaFaza="props">
            <q-td align="center">
              <q-badge outline color="mint">
                {{ props.row.tekovnaFazaOpis || ("Фаза: " + props.row.tekovnaFazaID) }}
              </q-badge>
            </q-td>
          </template>

          <!-- Предал / Примил -->
          <template v-slot:body-cell-Predal="props">
            <q-td align="center">{{ employeeMap[String(props.row.tekovenPredalID)] || "" }}</q-td>
          </template>

          <template v-slot:body-cell-Primil="props">
            <q-td align="center">{{ employeeMap[String(props.row.tekovenPrimilID)] || "" }}</q-td>
          </template>

          <!-- Забелешка -->
          <template v-slot:body-cell-Zabeleska="props">
            <q-td align="left">
              <div
                class="opis-preview"
                :class="{ 'note-red': hasNote(props.row.tekovnaZabeleska) }"
              >
                {{ shortText(props.row.tekovnaZabeleska, 50) }}
              </div>
            </q-td>
          </template>

          <!-- Акција -->
          <template v-slot:body-cell-Akcija="props">
            <q-td align="center" class="q-gutter-x-sm" @click.stop>
              <q-btn
                flat
                dense
                round
                color="mint"
                icon="edit"
                @click.stop="openManageDialog(null, props.row)"
              />
            </q-td>
          </template>
        </q-table>
      </q-card>

      <!-- DIALOG: ФАЗИ (ИСТОРИЈА) -->
      <q-dialog v-model="manageDialog.open" persistent>
        <q-card class="q-pa-lg" style="width: 1200px; max-width: 98vw;">
          <div class="row items-center justify-between q-mb-md">
            <div class="text-h6 text-mint text-bold">
              Фази (историја) — Барање: {{ formatBrojID(manageDialog.row?.ID) }}
            </div>

            <q-btn flat dense round icon="close" color="grey" @click="manageDialog.open = false" />
          </div>

          <q-banner v-if="manageDialog.isReadOnly" class="bg-grey-2 q-mb-md" rounded>
            Ова барање е во <b>одржување</b>. Дозволен е само преглед.
          </q-banner>

          <!-- ГОРЕН ДЕЛ -->
          <q-card flat bordered class="q-pa-md q-mb-md">
            <div class="row q-col-gutter-md">
              <div class="col-12 col-md-2">
                <q-input
                  :value="formatBrojID(manageDialog.row?.ID)"
                  label="Број"
                  outlined
                  dense
                  color="mint"
                  readonly
                />
              </div>

              <div class="col-12 col-md-3">
                <q-input
                  :value="manageDialog.row?.NazivPartner || ''"
                  label="Фирма"
                  outlined
                  dense
                  color="mint"
                  readonly
                />
              </div>

              <div class="col-12 col-md-3">
                <q-input
                  :value="manageDialog.row?.Baratel || ''"
                  label="Барател"
                  outlined
                  dense
                  color="mint"
                  readonly
                />
              </div>

              <div class="col-12 col-md-2">
                <q-input
                  :value="formatDisplayDate(manageDialog.row?.Datum)"
                  label="Датум"
                  outlined
                  dense
                  color="mint"
                  readonly
                />
              </div>

              <div class="col-12 col-md-2">
                <q-select
                  v-model="manageDialog.statusID"
                  :options="statusOptions"
                  label="Статус"
                  outlined
                  dense
                  color="mint"
                  emit-value
                  map-options
                  clearable
                  class="required-field"
                />
              </div>

              <div class="col-12 col-md-4">
                <q-input
                  :value="manageDialog.row?.OpisAplikacija || (aplikaciiMap[manageDialog.row?.AplikacijaID] || manageDialog.row?.AplikacijaID) || ''"
                  label="Апликација"
                  outlined
                  dense
                  color="mint"
                  readonly
                />
              </div>

              <div class="col-12 col-md-8">
                <q-input
                  :value="manageDialog.row?.Opis || ''"
                  label="Опис"
                  outlined
                  dense
                  color="mint"
                  type="textarea"
                  autogrow
                  readonly
                />
              </div>
            </div>
          </q-card>

          <!-- ТАБЕЛА: СИТЕ ФАЗИ -->
          <q-card flat bordered class="q-pa-md">
            <div class="text-subtitle1 text-mint text-bold q-mb-sm">Сите фази за барањето</div>

            <q-table
              :data="manageDialog.fazi"
              :columns="manageFaziColumns"
              row-key="pkid"
              flat
              dense
              class="rounded-xl table-clean clickable-table"
              no-data-label="Нема фази за ова барање."
              :loading="manageDialog.loading"
              @row-click="openEditPhase"
            >
              <template v-slot:body-cell-fazaID="props">
                <q-td align="center">{{ faziMap[props.row.fazaID] || props.row.fazaID }}</q-td>
              </template>

              <template v-slot:body-cell-predal="props">
                <q-td align="center">{{ employeeMap[String(props.row.predal)] || "" }}</q-td>
              </template>

              <template v-slot:body-cell-primil="props">
                <q-td align="center">{{ employeeMap[String(props.row.primil)] || "" }}</q-td>
              </template>

              <template v-slot:body-cell-predvidenDatum="props">
                <q-td align="center">
                  {{ props.row.predvidenDatum ? formatDisplayDate(props.row.predvidenDatum) : "" }}
                </q-td>
              </template>

              <template v-slot:body-cell-start="props">
                <q-td align="center">
                  {{ props.row.start ? formatDisplayDate(props.row.start) : "" }}
                </q-td>
              </template>

              <template v-slot:body-cell-end="props">
                <q-td align="center">
                  {{ props.row.end ? formatDisplayDate(props.row.end) : "" }}
                </q-td>
              </template>

              <template v-slot:body-cell-zabeleska="props">
                <q-td align="left">
                  <div
                    class="opis-preview"
                    :class="{ 'note-red': hasNote(props.row.zabeleska) }"
                  >
                    {{ shortText(props.row.zabeleska, 50) }}
                  </div>
                </q-td>
              </template>

              <template v-slot:body-cell-akcija="props">
                <q-td align="center" @click.stop>
                  <q-btn
                    flat
                    dense
                    round
                    icon="edit"
                    color="mint"
                    @click.stop="openEditPhase(null, props.row)"
                  >
                    <q-tooltip>
                      {{ manageDialog.isReadOnly ? 'Само преглед' : 'Измени Додај следна фаза' }}
                    </q-tooltip>
                  </q-btn>
                </q-td>
              </template>
            </q-table>

            <div class="row justify-end q-mt-sm">
              <div class="total-hours">
                Вкупно часови: <b>{{ manageTotalCasovi }}</b>
              </div>
            </div>

            <div class="row justify-center q-mt-md">
              <q-btn
                unelevated
                color="mint"
                icon="save"
                label="Зачувај барање"
                no-caps
                class="save-status-btn"
                :loading="manageDialog.statusSaving"
                :disable="!manageDialog.statusID"
                @click="saveBaranjeStatus"
              />
            </div>
          </q-card>

          <!-- DIALOG: ИЗМЕНИ -->
          <q-dialog v-model="editPhaseDialog.open" persistent>
            <q-card class="q-pa-lg" style="width: 980px; max-width: 95vw;">
              <div class="row items-center justify-between q-mb-md">
                <div class="text-h6 text-mint text-bold">
                  Измени / Заврши фаза — Барање: {{ formatBrojID(manageDialog.row?.ID) }}
                </div>
                <q-btn flat dense round icon="close" color="grey" @click="editPhaseDialog.open = false" />
              </div>

              <!-- 1) ИЗМЕНИ / ЗАВРШИ ТЕКОВНА -->
              <q-card flat bordered class="q-pa-md q-mb-md">
                <div class="text-subtitle1 text-mint text-bold q-mb-sm">Тековна фаза (измени / заврши)</div>

                <div class="row q-col-gutter-sm">
                  <div class="col-12 col-md-4">
                    <q-select
                      v-model="editPhaseDialog.form.fazaID"
                      :options="faziOptions"
                      label="Фаза"
                      outlined
                      dense
                      color="mint"
                      emit-value
                      map-options
                      :disable="true || manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-select
                      v-model="editPhaseDialog.form.predal"
                      :options="employeeOptions"
                      label="Предал"
                      outlined
                      dense
                      color="mint"
                      emit-value
                      map-options
                      :disable="true || manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-select
                      v-model="editPhaseDialog.form.primil"
                      :options="employeeOptions"
                      label="Примил"
                      outlined
                      dense
                      color="mint"
                      emit-value
                      map-options
                      :disable="true || manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-input
                      v-model="editPhaseDialog.form.predvidenDatum"
                      type="date"
                      label="Предвиден датум"
                      placeholder="dd-mm-yyyy"
                      outlined
                      dense
                      color="mint"
                      :disable="true || manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-input
                      v-model.number="editPhaseDialog.form.predvidenoVreme"
                      type="number"
                      step="0.25"
                      min="0"
                      label="Предвидено време"
                      outlined
                      dense
                      color="mint"
                      :disable="true || manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-input
                      v-model="editPhaseDialog.form.start"
                      type="date"
                      label="Почеток"
                      placeholder="dd-mm-yyyy"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-input
                      v-model="editPhaseDialog.form.end"
                      type="date"
                      label="Крај"
                      placeholder="dd-mm-yyyy"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly"
                      class="required-field"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-input
                      v-model.number="editPhaseDialog.form.casovi"
                      type="number"
                      step="0.25"
                      min="0"
                      label="Часови"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12 col-md-4">
                    <q-input
                      v-model.number="editPhaseDialog.form.efektivno"
                      type="number"
                      step="0.25"
                      min="0"
                      label="Ефективно"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly"
                    />
                  </div>

                  <div class="col-12">
                    <q-input
                      v-model="editPhaseDialog.form.zabeleska"
                      label="Забелешка"
                      outlined
                      dense
                      color="mint"
                      type="textarea"
                      autogrow
                      :disable="manageDialog.isReadOnly"
                      :input-class="hasNote(editPhaseDialog.form.zabeleska) ? 'note-red' : ''"
                    />
                  </div>
                </div>

                <div class="row justify-end q-mt-md">
                  <q-btn flat label="Откажи" color="grey" no-caps @click="editPhaseDialog.open = false" />
                  <q-btn
                    unelevated
                    label="Зачувај фаза"
                    color="mint"
                    no-caps
                    class="q-ml-sm"
                    :loading="editPhaseDialog.saving"
                    :disable="manageDialog.isReadOnly"
                    @click="saveEditedPhaseToBackend"
                  />
                </div>
              </q-card>

              <!-- 2) ДОДАДИ СЛЕДНА ФАЗА -->
              <q-card flat bordered class="q-pa-md">
                <div class="text-subtitle1 text-mint text-bold q-mb-sm">Додај следна фаза</div>

                <div class="row q-col-gutter-sm">
                  <div class="col-12 col-md-3">
                    <q-select
                      v-model="addForm.fazaID"
                      :options="faziOptions"
                      label="Фаза"
                      outlined
                      dense
                      color="mint"
                      emit-value
                      map-options
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                      class="required-field"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-select
                      v-model="addForm.predal"
                      :options="employeeOptions"
                      label="Предал"
                      outlined
                      dense
                      color="mint"
                      emit-value
                      map-options
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                      class="required-field"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-select
                      v-model="addForm.primil"
                      :options="employeeOptions"
                      label="Примил"
                      outlined
                      dense
                      color="mint"
                      emit-value
                      map-options
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-input
                      v-model="addForm.predvidenDatum"
                      type="date"
                      label="Предвиден датум"
                      placeholder="dd-mm-yyyy"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-input
                      v-model.number="addForm.predvidenoVreme"
                      type="number"
                      step="0.25"
                      min="0"
                      label="Предвидено време"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-input
                      v-model="addForm.start"
                      type="date"
                      label="Почеток"
                      placeholder="dd-mm-yyyy"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-input
                      v-model="addForm.end"
                      type="date"
                      label="Крај"
                      placeholder="dd-mm-yyyy"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-input
                      v-model.number="addForm.casovi"
                      type="number"
                      step="0.25"
                      min="0"
                      label="Часови"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    />
                  </div>

                  <div class="col-12 col-md-3">
                    <q-input
                      v-model.number="addForm.efektivno"
                      type="number"
                      step="0.25"
                      min="0"
                      label="Ефективно"
                      outlined
                      dense
                      color="mint"
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    />
                  </div>

                  <div class="col-12 col-md-9">
                    <q-input
                      v-model="addForm.zabeleska"
                      label="Забелешка"
                      outlined
                      dense
                      color="mint"
                      type="textarea"
                      autogrow
                      :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                      :input-class="hasNote(addForm.zabeleska) ? 'note-red' : ''"
                    />
                  </div>
                </div>

                <div class="row justify-end q-mt-sm">
                  <q-btn
                    label="Додади следна фаза"
                    color="mint"
                    unelevated
                    no-caps
                    :disable="manageDialog.isReadOnly || !editPhaseDialog.isLatest || editPhaseDialog.blockNextAdd"
                    @click="addNextPhaseFromEditDialog"
                  />
                </div>
              </q-card>
            </q-card>
          </q-dialog>
        </q-card>
      </q-dialog>

      <!-- File Preview -->
      <q-dialog v-model="fileDialog.open" maximized>
        <q-card class="q-pa-md bg-white">
          <div class="row justify-end">
            <q-btn dense flat round icon="close" color="red" @click="fileDialog.open = false" />
          </div>

          <div v-if="fileDialog.type === 'image'" class="text-center">
            <img :src="fileDialog.url" style="max-width: 100%; max-height: 90vh;" />
          </div>

          <div v-else class="text-center q-pa-xl">
            <q-btn color="mint" icon="download" label="Симни документ" @click="downloadFile(fileDialog.url)" />
          </div>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script>
import api, { FILE_BASE } from "@/api";
import { Notify } from "quasar";
import { getUser } from "@/services/db";
import { getVraboteniAPI } from "@/services/vraboteniAPI";
import { loadBaranjaAPI } from "@/services/baranjaAPI";
import { formatBroj } from "../../utils/dateFormat";

export default {
  name: "PoFazi",

  data() {
    return {
      user: getUser(),
      loading: false,
      rows: [],

      employeeOptions: [],
      employeeMap: {},
      aplikaciiMap: {},
      faziOptions: [],
      faziMap: {},

      statusOptions: [],
      statusiMap: {},
      faziOrderMap: {},
      odrzuvanjeFazaId: null,

      searchId: "",
      filterFazaId: null,
      filterPrimilId: null,

      fileDialog: { open: false, url: "", type: "" },

      manageDialog: {
        open: false,
        loading: false,
        row: null,
        isReadOnly: false,
        fazi: [],
        statusID: null,
        statusSaving: false
      },

      addForm: {
        fazaID: null,
        predal: null,
        primil: null,
        predvidenDatum: "",
        predvidenoVreme: null,
        start: "",
        end: "",
        casovi: null,
        efektivno: null,
        zabeleska: ""
      },

      editPhaseDialog: {
        open: false,
        saving: false,
        isLatest: false,
        blockNextAdd: true,
        form: {
          pkid: 0,
          fazaID: null,
          predal: null,
          primil: null,
          predvidenDatum: "",
          predvidenoVreme: null,
          start: "",
          end: "",
          casovi: null,
          efektivno: null,
          zabeleska: ""
        }
      },

      columns: [
        {
          name: "Broj",
          label: "Број",
          field: "ID",
          align: "center",
          sortable: true,
          format: (v) => this.formatBrojID(v)
        },
        { name: "PartnerID", label: "Фирма", field: "PartnerID", align: "left", sortable: true },
        { name: "Baratel", label: "Барател", field: "Baratel", align: "left", sortable: true },
        { name: "Opis", label: "Опис", field: "Opis", align: "left" },
        { name: "Datum", label: "Датум", field: "Datum", align: "center", sortable: true },
        { name: "AplikacijaID", label: "Апликација", field: "AplikacijaID", align: "center" },
        { name: "StepenItnost", label: "Степен итност", field: "StepenItnost", align: "center" },
        { name: "StatusID", label: "Статус", field: "StatusID", align: "center" },
        { name: "Files", label: "Документи", field: "Files", align: "center" },
        { name: "TekovnaFaza", label: "Тековна фаза", field: "tekovnaFazaID", align: "center" },
        { name: "Predal", label: "Предал", field: "tekovenPredalID", align: "center" },
        { name: "Primil", label: "Примил", field: "tekovenPrimilID", align: "center" },
        { name: "Zabeleska", label: "Забелешка", field: "tekovnaZabeleska", align: "left" },
        { name: "Akcija", label: "", field: "Akcija", align: "center" }
      ],

      manageFaziColumns: [
        { name: "fazaID", label: "Фаза", field: "fazaID", align: "center", sortable: true },
        { name: "predal", label: "Предал", field: "predal", align: "center" },
        { name: "primil", label: "Примил", field: "primil", align: "center" },
        { name: "predvidenDatum", label: "Предвиден датум", field: "predvidenDatum", align: "center" },
        { name: "predvidenoVreme", label: "Предвидено време", field: "predvidenoVreme", align: "center" },
        { name: "start", label: "Почеток", field: "start", align: "center" },
        { name: "end", label: "Крај", field: "end", align: "center" },
        { name: "casovi", label: "Часови", field: "casovi", align: "center" },
        { name: "efektivno", label: "Ефективно", field: "efektivno", align: "center" },
        { name: "zabeleska", label: "Забелешка", field: "zabeleska", align: "left" },
        { name: "akcija", label: "", field: "akcija", align: "center" }
      ]
    };
  },

  computed: {
    userFullName() {
      const u = this.user || {};

      const directName =
        (typeof u.ime === "string" && u.ime.trim() && !/^\d+$/.test(u.ime.trim()) ? u.ime.trim() : "") ||
        (typeof u.username === "string" && u.username.trim() && !/^\d+$/.test(u.username.trim()) ? u.username.trim() : "");

      if (directName) return directName;

      const candidates = [
        u.VrabID, u.VrabotenID, u.SifraID, u.id, u.userId,
        u.vrabID, u.vraboten, u.username, u.ime
      ];

      const rawId = candidates.find(v => v !== undefined && v !== null && String(v).trim() !== "");
      const key = rawId == null ? "" : String(rawId).trim();

      return this.employeeMap[key] || "Систем";
    },

    filtered() {
      const search = String(this.searchId || "").trim();

      const arr = this.rows.filter((r) => {
        const idOk =
          !search ||
          this.formatBrojID(r.ID).includes(search) ||
          String(r.ID).includes(search);
        const fazaOk = !this.filterFazaId || String(r.tekovnaFazaID) === String(this.filterFazaId);
        const primilOk = !this.filterPrimilId || String(r.tekovenPrimilID) === String(this.filterPrimilId);
        return idOk && fazaOk && primilOk;
      });

      arr.sort((a, b) => {
        const aId = Number(a.tekovnaFazaID) || 0;
        const bId = Number(b.tekovnaFazaID) || 0;

        const aIsOdr = this.odrzuvanjeFazaId != null && aId === this.odrzuvanjeFazaId;
        const bIsOdr = this.odrzuvanjeFazaId != null && bId === this.odrzuvanjeFazaId;

        if (aIsOdr && !bIsOdr) return 1;
        if (!aIsOdr && bIsOdr) return -1;

        const aOrder = this.faziOrderMap[String(aId)] ?? 9999;
        const bOrder = this.faziOrderMap[String(bId)] ?? 9999;

        if (aOrder !== bOrder) return aOrder - bOrder;
        return (Number(a.ID) || 0) - (Number(b.ID) || 0);
      });

      return arr;
    },

    activeCount() {
      return this.filtered.filter((r) => !this.isOdrzuvanje(r)).length;
    },

    odrzuvanjeCount() {
      return this.filtered.filter((r) => this.isOdrzuvanje(r)).length;
    },

    manageTotalCasovi() {
      const list = this.manageDialog.fazi || [];
      const sum = list.reduce((acc, x) => acc + (Number(x.casovi) || 0), 0);
      return Math.round(sum * 100) / 100;
    }
  },

  methods: {
    formatBroj,

    shortText(text, max = 55) {
      if (!text) return "";
      return text.length > max ? `${text.slice(0, max)}...` : text;
    },

    todayISO() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    formatBrojID(v, len = 8) {
      if (v == null) return "";

      const raw = String(v).includes("/") ? String(v).split("/")[0] : String(v);
      const digits = raw.replace(/\D/g, "");
      if (!digits) return "";

      return digits.padStart(len, "0");
    },

    normalizeBaranjeId(id) {
      return this.formatBrojID(id, 8);
    },

    formatBrojOnly(v, len = 8) {
      if (v == null) return "";

      const raw = String(v).includes("/") ? String(v).split("/")[0] : String(v);
      const digits = raw.replace(/\D/g, "");
      if (!digits) return "";

      return digits.padStart(len, "0");
    },

    getStatusText(row) {
      if (!row) return "";
      const id =
        row.StatusID ??
        row.StatusId ??
        row.statusID ??
        row.statusId ??
        row?.Status ??
        row?.status ??
        null;

      return this.statusiMap[String(id)] || "";
    },

    getStatusIdFromRow(row) {
      if (!row) return null;

      const id =
        row.StatusID ??
        row.StatusId ??
        row.statusID ??
        row.statusId ??
        row?.Status ??
        row?.status ??
        null;

      return id == null || id === "" ? null : Number(id);
    },

    computeReadOnly(row) {
      return this.isZavrseno(row) || this.isOdrzuvanje(row);
    },

    syncManageDialogAfterReload() {
      if (!this.manageDialog.open || !this.manageDialog.row) return;

      const fresh = this.rows.find((x) => Number(x.ID) === Number(this.manageDialog.row.ID));
      if (!fresh) return;

      this.manageDialog.row = fresh;
      this.manageDialog.statusID = this.getStatusIdFromRow(fresh);
      this.manageDialog.isReadOnly = this.computeReadOnly(fresh);
    },

    itnostClass(val) {
      const v = Number(val);
      if (!v) return "itnost-none";
      if (v === 1) return "itnost-low";
      if (v === 2) return "itnost-mid";
      if (v >= 3) return "itnost-high";
      return "itnost-none";
    },

    formatDisplayDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    },

    hasNote(val) {
      return String(val || "").trim().length > 0;
    },

    isOdrzuvanje(row) {
      const id = Number(row?.tekovnaFazaID) || 0;
      return this.odrzuvanjeFazaId != null && id === this.odrzuvanjeFazaId;
    },

    isZavrseno(row) {
      const id = this.getStatusIdFromRow(row);
      const s = String(this.statusiMap[String(id)] || "").toLowerCase();
      return s.includes("заврш") || s.includes("затвор");
    },

    onSearchIdInput(val) {
      const onlyDigits = String(val || "").replace(/\D/g, "").slice(0, 10);
      if (onlyDigits !== this.searchId) this.searchId = onlyDigits;
    },

    toISODate(value) {
      if (!value) return "";
      if (String(value).includes("T")) return String(value).split("T")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);

      const parts = String(value).split(" ");
      const datePart = parts[0];
      const [day, mon, year2] = datePart.split("-");

      const months = {
        Jan: "01",
        Feb: "02",
        Mar: "03",
        Apr: "04",
        May: "05",
        Jun: "06",
        Jul: "07",
        Aug: "08",
        Sep: "09",
        Oct: "10",
        Nov: "11",
        Dec: "12"
      };

      const mm = months[mon];
      if (!mm) return "";
      const yyyy = year2.length === 2 ? `20${year2}` : year2;
      return `${yyyy}-${mm}-${day}`;
    },

    async fetchAllFazi(baranjeId) {
      try {
        const BID = this.normalizeBaranjeId(baranjeId);

        const res = await api.get("/baranja/get-faza", {
          params: {
            BaranjeID: BID,
            _: Date.now()
          }
        });

        const data = res.data;
        const list = Array.isArray(data) ? data : [];

        const mapped = list.map((f) => {
          const zabeleskaVal =
            (f?.Zabeleska ??
              f?.zabeleska ??
              f?.ZabeleskaText ??
              f?.ZabeleskaOpis ??
              "") || "";

          return {
            pkid: Number(f.pkid) || 0,
            fazaID: f.FazaID ? Number(f.FazaID) : null,
            predal: f.PredalVrabID ? Number(f.PredalVrabID) : null,
            primil: f.PrimilVrabID ? Number(f.PrimilVrabID) : null,
            predvidenDatum: this.toISODate(f.DatumPredviden),
            predvidenoVreme:
              f.VremePred === "" || f.VremePred == null ? null : Number(f.VremePred),
            start: this.toISODate(f.DatumPocetok),
            end: this.toISODate(f.DatumKraj),
            casovi: f.Vreme === "" || f.Vreme == null ? null : Number(f.Vreme),
            efektivno:
              f.Efektivno === "" || f.Efektivno == null ? null : Number(f.Efektivno),
            zabeleska: zabeleskaVal
          };
        });

        mapped.sort((a, b) => (Number(a.pkid) || 0) - (Number(b.pkid) || 0));
        return mapped;
      } catch (e) {
        console.error("fetchAllFazi error", e);
        return [];
      }
    },

    async loadVraboteni() {
      const apiData = await getVraboteniAPI();

      this.employeeOptions = apiData.map((v) => ({
        label: v.Naziv,
        value: v.SifraID
      }));

      this.employeeMap = Object.fromEntries(
        apiData.map((v) => [String(v.SifraID), v.Naziv])
      );
    },

    async loadStatusi() {
      const res = await api.get("/statusi");
      const data = Array.isArray(res.data) ? res.data : [];

      this.statusOptions = data.map((s) => ({
        label: s.Opis,
        value: s.SifraID
      }));

      this.statusiMap = Object.fromEntries(
        data.map((s) => [String(s.SifraID), s.Opis])
      );
    },

    async loadAplikacii() {
      const res = await api.get("/aplikacii");
      const data = res.data;
      this.aplikaciiMap = Object.fromEntries(data.map((a) => [a.SifraID, a.Opis]));
    },

    async loadFazi() {
      const res = await api.get("/get/fazi");
      const data = res.data;

      this.faziOptions = data.map((f) => ({ label: f.Opis, value: f.SifraID }));
      this.faziMap = Object.fromEntries(data.map((f) => [f.SifraID, f.Opis]));

      this.faziOrderMap = {};
      data.forEach((f, idx) => {
        this.faziOrderMap[String(f.SifraID)] = idx;
      });

      const odr = data.find((f) => String(f.Opis || "").toLowerCase().includes("одрж"));
      this.odrzuvanjeFazaId = odr ? Number(odr.SifraID) : null;
    },

    async loadActivePoFazi() {
      const apiData = await loadBaranjaAPI();
      const list = Array.isArray(apiData) ? apiData : apiData?.Items || apiData?.List || [];

      const base = list.map((item) => ({
        ID: this.normalizeBaranjeId(item.ID),
        PartnerID:
          item.PartnerID ??
          item.PartnerId ??
          item.FirmaID ??
          item.FirmaId ??
          item.KomintentID ??
          item.KomintentId ??
          item.Partner ??
          item.Firma ??
          null,
        NazivPartner: item.NazivPartner || "",
        Baratel: item.Baratel,
        Opis: item.Opis,
        Datum: item.Datum,
        AplikacijaID: item.AplikacijaID,
        OpisAplikacija: item.OpisAplikacija || "",
        StatusID: item.StatusID ?? item.StatusId ?? item.statusID ?? item.statusId ?? null,
        StepenItnost: item.StepenItnost,
        Files: item.Files || []
      }));

      const enriched = await Promise.all(
        base.map(async (r) => {
          const all = await this.fetchAllFazi(r.ID);
          const last = all.length ? all[all.length - 1] : null;

          const tekovnaFazaID = last?.fazaID ? Number(last.fazaID) : 0;

          return {
            ...r,
            tekovnaFazaID,
            tekovnaFazaOpis: this.faziMap[tekovnaFazaID] || "",
            tekovenPrimilID: last?.primil ?? null,
            tekovenPredalID: last?.predal ?? null,
            tekovnaZabeleska: last?.zabeleska || ""
          };
        })
      );

      this.rows = enriched.filter((x) => Number(x.tekovnaFazaID) > 0);
    },

    async reloadAll() {
      this.loading = true;
      try {
        await this.loadActivePoFazi();
        this.syncManageDialogAfterReload();
      } catch (e) {
        console.error(e);
        Notify.create({ type: "negative", message: "Грешка при вчитување Преглед по фази." });
      } finally {
        this.loading = false;
      }
    },

    resetAddForm() {
      this.addForm = {
        fazaID: null,
        predal: null,
        primil: null,
        predvidenDatum: "",
        predvidenoVreme: null,
        start: "",
        end: "",
        casovi: null,
        efektivno: null,
        zabeleska: ""
      };
    },

    async openManageDialog(evt, row) {
      const targetRow = row || evt;
      if (!targetRow) return;

      this.manageDialog.open = true;
      this.manageDialog.loading = true;
      this.manageDialog.row = targetRow;
      this.manageDialog.statusID = this.getStatusIdFromRow(targetRow);
      this.manageDialog.isReadOnly = this.computeReadOnly(targetRow);

      try {
        this.manageDialog.fazi = await this.fetchAllFazi(targetRow.ID);
      } catch (e) {
        console.error(e);
        this.manageDialog.fazi = [];
      } finally {
        this.manageDialog.loading = false;
      }
    },

    async saveBaranjeStatus() {
      const row = this.manageDialog.row;

      if (!row) {
        Notify.create({ type: "warning", message: "Нема избрано барање." });
        return;
      }

      if (!this.manageDialog.statusID) {
        Notify.create({ type: "warning", message: "Избери статус." });
        return;
      }

      this.manageDialog.statusSaving = true;

      try {
        const payload = {
          ID: this.normalizeBaranjeId(row.ID),
          StatusID: Number(this.manageDialog.statusID),
          UserID: Number(this.user?.id) || 0
        };

        await api.post("/baranja/updateStatus", payload, {
          headers: { "Content-Type": "application/json" }
        });

        Notify.create({
          type: "positive",
          message: "Барањето е успешно зачувано со избраниот статус."
        });

        this.manageDialog.row = {
          ...this.manageDialog.row,
          StatusID: Number(this.manageDialog.statusID)
        };

        await this.reloadAll();
      } catch (err) {
        console.error("saveBaranjeStatus error", err);

        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Грешка при зачувување на статусот.";

        Notify.create({ type: "negative", message: msg });
      } finally {
        this.manageDialog.statusSaving = false;
      }
    },

    computeBlockNextAdd(editForm) {
      const isOdr =
        this.odrzuvanjeFazaId != null &&
        Number(editForm?.fazaID) === Number(this.odrzuvanjeFazaId);

      const hasEnd = String(editForm?.end || "").trim().length > 0;

      return !hasEnd && !isOdr;
    },

    async addNextPhaseFromEditDialog() {
      const row = this.manageDialog.row;
      const d = this.addForm;
      if (!row) return;

      if (this.manageDialog.isReadOnly) {
        Notify.create({ type: "info", message: "Барањето е во одржување. Дозволен е само преглед." });
        return;
      }

      if (!this.editPhaseDialog.isLatest) {
        Notify.create({ type: "warning", message: "Следна фаза се додава од последната фаза." });
        return;
      }

      if (this.editPhaseDialog.blockNextAdd) {
        Notify.create({ type: "warning", message: "Внеси Крај на фазата." });
        return;
      }

      if (!d.fazaID) return Notify.create({ type: "warning", message: "Избери Фаза." });

      if (!d.predal) d.predal = this.editPhaseDialog.form.primil ?? null;
      if (!d.predal) return Notify.create({ type: "warning", message: "Нема Примил на тековната фаза (за автоматски Предал)." });

      try {
        const payload = {
          CurrentUserId: Number(this.user?.id) || 0,
          Items: [
            {
              pkid: 0,
              Info: 1,
              BaranjeID: this.normalizeBaranjeId(row.ID),
              FazaID: Number(d.fazaID) || 0,
              PredalVrabID: d.predal ? Number(d.predal) : null,
              PrimilVrabID: d.primil ? Number(d.primil) : null,
              DatumPredviden: d.predvidenDatum || null,
              DatumPocetok: d.start || null,
              DatumKraj: d.end || null,
              VremePred:
                d.predvidenoVreme === "" || d.predvidenoVreme == null ? null : Number(d.predvidenoVreme),
              Vreme: d.casovi === "" || d.casovi == null ? null : Number(d.casovi),
              Efektivно: d.efektivно === "" || d.efektivно == null ? null : Number(d.efektivно),
              Zabeleska: d.zabeleska || ""
            }
          ]
        };

        let data;

        try {
          const res = await api.post("/baranja/save-fazi", payload, {
            headers: { "Content-Type": "application/json" }
          });

          data = res.data;
        } catch (err) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.Message ||
            err?.message ||
            "Грешка при додавање фаза!";

          Notify.create({
            type: "negative",
            message: msg
          });
          return;
        }

        Notify.create({ type: "positive", message: "Следната фаза е додадена!" });

        this.resetAddForm();

        this.manageDialog.loading = true;
        this.manageDialog.fazi = await this.fetchAllFazi(row.ID);
        this.manageDialog.loading = false;

        await this.reloadAll();
      } catch (e) {
        console.error(e);
        Notify.create({ type: "negative", message: "Грешка при додавање фаза!" });
      }
    },

    openEditPhase(evt, fazaRow) {
      const targetRow = fazaRow || evt;
      if (!this.manageDialog.row?.ID || !targetRow?.pkid) {
        Notify.create({ type: "warning", message: "Недостасува pkid за уредување." });
        return;
      }

      const last = this.manageDialog.fazi?.length
        ? this.manageDialog.fazi[this.manageDialog.fazi.length - 1]
        : null;

      const isLatest = last && Number(last.pkid) === Number(targetRow.pkid);

      this.editPhaseDialog.form = {
        pkid: Number(targetRow.pkid) || 0,
        fazaID: targetRow.fazaID ? Number(targetRow.fazaID) : null,
        predal: targetRow.predal ?? null,
        primil: targetRow.primil ?? null,
        predvidenDatum: targetRow.predvidenDatum || "",
        predvidenoVreme: targetRow.predvidenoVreme ?? null,
        start: targetRow.start || "",
        end: targetRow.end || "",
        casovi: targetRow.casovi ?? null,
        efektivно: targetRow.efektivно ?? null,
        zabeleska: targetRow.zabeleska || ""
      };

      this.editPhaseDialog.isLatest = !!isLatest;
      this.editPhaseDialog.blockNextAdd = this.computeBlockNextAdd(this.editPhaseDialog.form);

      this.resetAddForm();
      this.addForm.predal = this.editPhaseDialog.form.primil ?? null;

      this.editPhaseDialog.open = true;
    },

    async saveEditedPhaseToBackend() {
      const row = this.manageDialog.row;
      const d = this.editPhaseDialog.form;

      if (!row || !d.pkid) return Notify.create({ type: "negative", message: "Недостасува pkid за уредување." });

      if (this.manageDialog.isReadOnly) {
        Notify.create({ type: "info", message: "Барањето е завршено/затворено." });
        return;
      }

      if (!d.fazaID) return Notify.create({ type: "warning", message: "Избери Фаза." });

      this.editPhaseDialog.saving = true;

      try {
        const payload = {
          CurrentUserId: Number(this.user?.id) || 0,
          Items: [
            {
              pkid: Number(d.pkid) || 0,
              Info: 2,
              BaranjeID: this.normalizeBaranjeId(row.ID),
              FazaID: Number(d.fazaID) || 0,
              PredalVrabID: d.predal ? Number(d.predal) : null,
              PrimilVrabID: d.primil ? Number(d.primil) : null,
              DatumPredviden: d.predvidenDatum || null,
              DatumPocetok: d.start || null,
              DatumKraj: d.end || null,
              VremePred:
                d.predvidenoVreme === "" || d.predvidenoVreme == null ? null : Number(d.predvidenoVreme),
              Vreme: d.casovi === "" || d.casovi == null ? null : Number(d.casovi),
              Efektivно: d.efektivно === "" || d.efektivно == null ? null : Number(d.efektivно),
              Zabeleska: d.zabeleska || ""
            }
          ]
        };

        let data;

        try {
          const res = await api.post("/baranja/save-fazi", payload, {
            headers: { "Content-Type": "application/json" }
          });

          data = res.data;
        } catch (err) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.Message ||
            err?.message ||
            "Грешка при зачувување!";

          Notify.create({
            type: "negative",
            message: msg
          });
          return;
        }

        Notify.create({ type: "positive", message: "Фазата е успешно ажурирана!" });

        this.manageDialog.loading = true;
        this.manageDialog.fazi = await this.fetchAllFazi(this.normalizeBaranjeId(row.ID));
        this.manageDialog.loading = false;

        await this.reloadAll();

        this.editPhaseDialog.blockNextAdd = this.computeBlockNextAdd(this.editPhaseDialog.form);
      } catch (e) {
        console.error(e);
        Notify.create({ type: "negative", message: "Грешка при зачувување!" });
      } finally {
        this.editPhaseDialog.saving = false;
      }
    },

    previewFile(filePath) {
      const fullUrl = FILE_BASE + filePath;
      const isImg = /\.(png|jpg|jpeg|gif|webp)$/i.test(filePath);
      const isDoc = /\.(pdf|txt|doc|docx|xlsx)$/i.test(filePath);

      if (isImg) {
        this.fileDialog.url = fullUrl;
        this.fileDialog.type = "image";
        this.fileDialog.open = true;
      } else if (isDoc) {
        this.downloadFile(fullUrl);
      } else {
        window.open(fullUrl, "_blank");
      }
    },

    downloadFile(url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = url.split("/").pop();
      a.click();
    }
  },

  async mounted() {
    this.user = getUser();
    this.loading = true;

    try {
      await this.loadAplikacii();
      await this.loadStatusi();
      await this.loadVraboteni();
      await this.loadFazi();
      await this.reloadAll();
    } catch (e) {
      console.error(e);
      Notify.create({ type: "negative", message: "Грешка при стартно вчитување." });
    } finally {
      this.loading = false;
    }
  },

  async activated() {
    await this.reloadAll();
  }
};
</script>

<style scoped>
.bg-page { background-color: #f7faf9; min-height: 100vh; }
.container-xl { max-width: 1180px; margin: 0 auto; }
.text-mint { color: #0e6b5f; }
.header-section { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; }

.info-strip {
  background: white;
  border: 1px solid #d6ebe6;
  border-radius: 12px;
  padding: 10px 18px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  color: #0e6b5f;
}

.info-item { display: flex; align-items: center; gap: 6px; }
.filter-card, .table-card { background: white; border-radius: 14px; }
.q-table thead tr { background-color: #f1f6f5; color: #0e6b5f; font-weight: 600; }
.q-table tbody tr:hover { background-color: #f6fdfb; transition: 0.2s ease; }
.note-red { color: #d32f2f !important; font-weight: 400 !important; }

.total-hours {
  background: #f1f6f5;
  border: 1px solid #d6ebe6;
  border-radius: 10px;
  padding: 6px 10px;
  color: #0e6b5f;
  font-size: 13px;
}

.btn-next-noborder {
  background: #ffffff !important;
  color: #0e6b5f !important;
  border-radius: 8px;
  padding: 2px 8px !important;
  font-size: 12px;
  min-height: 28px;
  box-shadow: none !important;
}

:deep(.required-field .q-field__label) {
  display: inline-flex !important;
  align-items: center !important;
}

:deep(.required-field .q-field__label::after) {
  content: " *";
  color: #e53935 !important;
  font-weight: 700;
}

.clickable-table :deep(tbody tr) {
  cursor: pointer;
}

.opis-preview {
  max-width: 230px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.save-status-btn {
  min-width: 190px;
  height: 38px;
  font-weight: 600;
  border-radius: 8px;
}

</style>